import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import {
  BAND_LABEL_ID_BY_TONE,
  EMPTY_VALUE,
  RECOMMENDATIONS_ANCHOR,
  TONE,
  getToneFromStatus,
} from '../../data/constants'
import { getEntitiesFlaggedCount, getFullDeploymentHours } from '../../data/reportModel'
import { useAIRiskReportExportMode } from '../../components/RenderMode/AIRiskReportRenderModeProvider'
import styles from './OverviewHero.module.scss'

function ScoreValue({ score, tone, className = '' }) {
  return <div className={`${styles.scoreValue} ${styles[tone] ?? ''} ${className}`}>{score ?? EMPTY_VALUE}</div>
}

ScoreValue.propTypes = {
  score: PropTypes.number,
  tone: PropTypes.oneOf(Object.values(TONE)).isRequired,
  className: PropTypes.string,
}

export default function OverviewHero({ assessment = {}, summary = {}, quickWins = [], findings = [] }) {
  const isExportMode = useAIRiskReportExportMode()
  const score = assessment.score ?? null
  const tone = getToneFromStatus(assessment.status)
  const projected = assessment.projectedAfterRemediation ?? { score, status: assessment.status }
  const projectedTone = getToneFromStatus(projected.status)
  const fullDeploymentHours = getFullDeploymentHours(summary)
  const recommendationTileClassName = [
    styles.smallTile,
    !isExportMode ? styles.smallTileLink : '',
    'd-flex flex-column gap-2',
  ]
    .filter(Boolean)
    .join(' ')
  const recommendationTileContent = (
    <>
      <div className={styles.tileLabel}>
        <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.RECOMMENDATIONS_TO_ADDRESS" />
      </div>
      <div className={`${styles.bigNumber} ${styles.info} mt-auto`}>{quickWins.length}</div>
    </>
  )

  return (
    <div className={`${styles.grid} d-grid`}>
      <div className={`${styles.overallTile} ${styles[tone] ?? ''} d-flex flex-column gap-3`}>
        <div className={styles.tileLabel}>
          <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.OVERALL_POSTURE" />
        </div>
        <div>
          <div className={styles.overallScore}>
            {score ?? EMPTY_VALUE}
            {score !== null && <small>%</small>}
          </div>
          <div className={styles.bandLabel}>
            <FormattedMessage id={BAND_LABEL_ID_BY_TONE[tone]} />
          </div>
        </div>
        <div className={`${styles.legend} d-flex flex-wrap mt-auto`}>
          <span className="d-inline-flex align-items-center gap-1">
            <i className={styles.ok} /> <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.LEGEND_STRONG" />
          </span>
          <span className="d-inline-flex align-items-center gap-1">
            <i className={styles.warn} /> <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.LEGEND_MODERATE" />
          </span>
          <span className="d-inline-flex align-items-center gap-1">
            <i className={styles.danger} /> <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.LEGEND_GAPS" />
          </span>
        </div>
      </div>

      {isExportMode ? (
        <div className={recommendationTileClassName}>{recommendationTileContent}</div>
      ) : (
        <a href={`#${RECOMMENDATIONS_ANCHOR}`} className={recommendationTileClassName}>
          {recommendationTileContent}
        </a>
      )}

      <div className={`${styles.smallTile} d-flex flex-column gap-2`}>
        <div className={styles.tileLabel}>
          <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.AFTER_RECOMMENDATIONS" />
        </div>
        <div className={`${styles.projectedFlow} d-flex align-items-center mt-auto`}>
          <ScoreValue score={score} tone={tone} />
          <span className={styles.projectedArrow} aria-hidden="true">
            →
          </span>
          <ScoreValue score={projected.score} tone={projectedTone} />
        </div>
      </div>

      <div className={`${styles.smallTile} d-flex flex-column gap-2`}>
        <div className={styles.tileLabel}>
          <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.ENTITIES_FLAGGED" />
        </div>
        <div className={`${styles.bigNumber} mt-auto`}>{getEntitiesFlaggedCount(findings)}</div>
      </div>

      <div className={`${styles.smallTile} d-flex flex-column gap-2`}>
        <div className={styles.tileLabel}>
          <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.PRODUCTIVITY_CAPACITY" />
        </div>
        <div className={`${styles.bigNumber} mt-auto`}>
          {fullDeploymentHours > 0 ? `~${fullDeploymentHours}` : EMPTY_VALUE}{' '}
          {fullDeploymentHours > 0 && (
            <small>
              <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.HOURS_PER_WEEK" />
            </small>
          )}
        </div>
      </div>
    </div>
  )
}

OverviewHero.propTypes = {
  assessment: PropTypes.shape({
    score: PropTypes.number,
    status: PropTypes.string,
    projectedAfterRemediation: PropTypes.shape({ score: PropTypes.number, status: PropTypes.string }),
  }),
  summary: PropTypes.shape({}),
  quickWins: PropTypes.arrayOf(PropTypes.shape({})),
  findings: PropTypes.arrayOf(PropTypes.shape({})),
}
